import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

interface User {
  id: number;
  username: string;
  nombre: string;
  titulo?: string | null;
  codigo_minsa?: string | null;
  rol: string;
  medico_id?: number | null;
  requiresPasswordChange?: boolean;
  ubicacion_id?: number | null;
  ubicacion_codigo?: string | null;
  ubicacion_nombre?: string | null;
}

/** Super usuario: acceso a todo el sistema y todos los módulos (Luisoft o rol superusuario). */
export function isSuperUsuario(user: User | null): boolean {
  return !!(user && (user.username === 'Luisoft' || user.rol === 'superusuario'));
}

/** Usuario con permisos de administrador o super usuario. */
export function isAdminOrSuper(user: User | null): boolean {
  return !!(user && (user.rol === 'administrador' || isSuperUsuario(user)));
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  permisos: string[];
  requiresPasswordChange: boolean;
  isSuperUsuario: boolean;
  isAdminOrSuper: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  clearRequiresPasswordChange: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [permisos, setPermisos] = useState<string[]>([]);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [loading, setLoading] = useState(true);

  const verifyToken = useCallback(async () => {
    if (!token) {
      setLoading(false);
      setPermisos([]);
      setRequiresPasswordChange(false);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000,
      });
      const u = response.data.user;
      setPermisos(Array.isArray(response.data.permisos) ? response.data.permisos : []);
      setRequiresPasswordChange(!!u?.requiresPasswordChange);
      const meRes = await axios.get(`${API_URL}/usuarios/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000,
      });
      const me = meRes.data;
      setUser({
        id: me.id,
        username: me.username,
        nombre: me.nombre ?? '',
        titulo: me.titulo ?? null,
        codigo_minsa: me.codigo_minsa ?? null,
        rol: me.rol,
        medico_id: me.medico_id ?? null,
        requiresPasswordChange: u?.requiresPasswordChange ?? false,
        ubicacion_id: me.ubicacion_id ?? null,
        ubicacion_codigo: me.ubicacion_codigo ?? null,
        ubicacion_nombre: me.ubicacion_nombre ?? null,
      });
    } catch (error) {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      setPermisos([]);
      setRequiresPasswordChange(false);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const login = async (username: string, password: string) => {
    const response = await axios.post(`${API_URL}/auth/login`, {
      username: String(username ?? '').trim(),
      password: String(password ?? '').trim(),
    });
    const { token: newToken, permisos: newPermisos, requiresPasswordChange: rpc } = response.data;
    setToken(newToken);
    setPermisos(Array.isArray(newPermisos) ? newPermisos : []);
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    const meRes = await axios.get(`${API_URL}/usuarios/me`, {
      headers: { Authorization: `Bearer ${newToken}` },
    });
    const me = meRes.data;
    setUser({
      id: me.id,
      username: me.username,
      nombre: me.nombre ?? '',
      titulo: me.titulo ?? null,
      codigo_minsa: me.codigo_minsa ?? null,
      rol: me.rol,
      medico_id: me.medico_id ?? null,
      requiresPasswordChange: !!rpc || !!me.requiresPasswordChange,
      ubicacion_id: me.ubicacion_id ?? null,
      ubicacion_codigo: me.ubicacion_codigo ?? null,
      ubicacion_nombre: me.ubicacion_nombre ?? null,
    });
    setRequiresPasswordChange(!!rpc || !!me.requiresPasswordChange);
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setPermisos([]);
    setRequiresPasswordChange(false);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    await axios.post(`${API_URL}/auth/change-password`, { currentPassword, newPassword }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setRequiresPasswordChange(false);
    setUser((prev) => (prev ? { ...prev, requiresPasswordChange: false } : null));
    await verifyToken();
  };

  const clearRequiresPasswordChange = () => setRequiresPasswordChange(false);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token]);

  const isSuper = isSuperUsuario(user);
  const isAdmin = isAdminOrSuper(user);

  return (
    <AuthContext.Provider value={{ user, token, permisos, requiresPasswordChange, isSuperUsuario: isSuper, isAdminOrSuper: isAdmin, login, logout, changePassword, clearRequiresPasswordChange, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

